/**
 * Product picker for a collection.
 *
 * The wireframe draws only a dashed "+ Add products from catalog" box — the
 * picker itself was never designed. This is the smallest honest version: search
 * the catalog, tick what belongs, add. It reuses `storeCatalog.list` rather
 * than adding an endpoint, so it sees exactly what the products screen sees.
 *
 * Products already in the collection are shown ticked and disabled, so the
 * operator can tell "already in" from "not chosen" — the junction is
 * idempotent, but the UI shouldn't make them guess.
 */
import { useState } from "react";
import { Package, Search } from "lucide-react";
import {
  Button,
  DialogRoot,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Input,
  Skeleton,
} from "@flint/ui";
import { trpc } from "../../../lib/trpc.ts";

const PAGE_SIZE = 50;

/**
 * The slice of a catalog row a caller needs to do something with the picks.
 * Collections only needs the ids; the quote line editor also needs the price,
 * so the chosen rows come back alongside them rather than forcing a second
 * lookup of data this dialog already holds.
 */
export interface PickedProduct {
  listingId: string;
  name: string;
  sku: string | null;
  /** Null when the item carries no default rate — the server prices it at 0. */
  basePricePaise: string | null;
  /**
   * Every way this product can be bought (ADR-0051), each entry naming a library
   * pack and its per-pack price. Carried so the quote form can offer the pack
   * and price the line from it; the collections caller ignores it.
   */
  pricingEntries: PickedPricingEntry[];
}

/** The slice of a listing's pricing entry the quote form needs. */
export interface PickedPricingEntry {
  packId?: string | undefined;
  label: string;
  pieces: number;
  pricePaise?: string | undefined;
  discountBps?: number | undefined;
}

export interface AddProductsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Listing ids already in the collection — ticked and locked. */
  alreadyIn: string[];
  busy: boolean;
  /** `picked` carries the same rows as `listingIds`, in the same order. */
  onAdd: (listingIds: string[], picked: PickedProduct[]) => void;
  /** Overrides the dialog heading — "Add products" reads oddly on a quote. */
  title?: string;
}

export function AddProductsDialog({
  open,
  onOpenChange,
  alreadyIn,
  busy,
  onAdd,
  title = "Add products",
}: AddProductsDialogProps) {
  const [q, setQ] = useState("");
  const [picked, setPicked] = useState<Set<string>>(new Set());

  const list = trpc.storeCatalog.list.useQuery(
    { tab: "all", limit: PAGE_SIZE, ...(q ? { q } : {}) },
    { enabled: open },
  );
  const rows = list.data?.items ?? [];
  const already = new Set(alreadyIn);

  function toggle(listingId: string) {
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(listingId)) next.delete(listingId);
      else next.add(listingId);
      return next;
    });
  }

  return (
    <DialogRoot
      open={open}
      onOpenChange={(next: boolean) => {
        if (!next) {
          setQ("");
          setPicked(new Set());
        }
        onOpenChange(next);
      }}
    >
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>

        <div className="relative">
          <Search
            size={14}
            aria-hidden
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted"
          />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name, SKU…"
            className="h-9 pl-8"
            aria-label="Search products"
          />
        </div>

        <div className="mt-3 max-h-80 overflow-y-auto rounded border border-line">
          {list.isLoading ? (
            <div className="space-y-2 p-3">
              {Array.from({ length: 4 }).map((_, i) => (
                <Skeleton key={i} className="h-8 w-full" />
              ))}
            </div>
          ) : rows.length === 0 ? (
            <p className="px-3 py-10 text-center text-sm text-muted">
              {q ? "No products match that search." : "No products in the catalog yet."}
            </p>
          ) : (
            <ul>
              {rows.map((r) => {
                const inAlready = already.has(r.listingId);
                const checked = inAlready || picked.has(r.listingId);
                return (
                  <li key={r.listingId} className="border-b border-line last:border-0">
                    <label
                      className={`flex cursor-pointer items-center gap-3 px-3 py-2 hover:bg-raised ${
                        inAlready ? "cursor-not-allowed opacity-60" : ""
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        disabled={inAlready}
                        onChange={() => toggle(r.listingId)}
                        className="h-4 w-4 shrink-0 accent-sky-600"
                      />
                      {r.images[0] ? (
                        <img
                          src={r.images[0]}
                          alt=""
                          className="h-8 w-8 shrink-0 rounded-sm border border-line object-cover"
                        />
                      ) : (
                        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-sm border border-line bg-raised text-muted">
                          <Package className="h-4 w-4" aria-hidden />
                        </span>
                      )}
                      <span className="min-w-0 flex-1 truncate text-sm text-ink">{r.name}</span>
                      <span className="shrink-0 font-mono text-xs text-muted">{r.sku ?? "—"}</span>
                      {inAlready ? (
                        <span className="shrink-0 text-xs text-muted">Already in</span>
                      ) : null}
                    </label>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <DialogFooter>
          <Button intent="secondary" size="md" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            intent="primary"
            size="md"
            disabled={busy || picked.size === 0}
            onClick={() => {
              const ids = [...picked];
              const byId = new Map(rows.map((r) => [r.listingId, r]));
              onAdd(
                ids,
                ids.flatMap((id) => {
                  const r = byId.get(id);
                  // A pick can only come from a row that was on screen, so this
                  // filter is belt-and-braces against a list refetch mid-pick.
                  return r
                    ? [
                        {
                          listingId: r.listingId,
                          name: r.name,
                          sku: r.sku,
                          basePricePaise: r.basePricePaise,
                          pricingEntries: r.pricingEntries ?? [],
                        },
                      ]
                    : [];
                }),
              );
            }}
          >
            {picked.size > 0 ? `Add ${picked.size}` : "Add"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </DialogRoot>
  );
}
